import { z } from 'zod';
import { MediaTypeEnum } from './media-unit-variants.js';
import {
  COST_RANGE_DIMENSIONS,
  validateRangeFieldPairs,
  type CostRangeDimensionId,
} from './media-unit-cost-range-dimensions.js';

/**
 * Shared filter field definitions used by both MediaUnitFiltersSchema
 * and MapPinsQuerySchema. Adding a new filter here propagates to both,
 * including the numeric range (area/width/height/audience/cost dimensions),
 * select/text (material/multiformat/tags/address/notes/roadNumber), boolean
 * (noPhoto), and date (availabilityFrom/To) dimensions (map-catalog-filter-parity).
 *
 * Note: `status` is intentionally excluded -- the two schemas use different
 * Zod types for it (plain string array vs. typed ReservationStatus array).
 * `search`, `importJobId`, and `exclude` also stay out — each schema declares
 * `search` independently (identical shape, not worth sharing), and
 * `importJobId`/`exclude` are genuinely catalog-only.
 */
export const filterArrayField = z.array(z.string()).min(1);

/** YYYY-MM-DD calendar date string used by the availability range filter. */
export const isoDateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

/**
 * Coerces the URL query-string boolean form correctly:
 * - "true" / "1" -> true, anything else (e.g. "false", "") -> false, absent -> undefined.
 * - z.coerce.boolean() maps "false" (a non-empty string) to true, so we transform instead.
 */
export const queryBoolean = z
  .string()
  .transform((v) => v === 'true' || v === '1')
  .or(z.boolean())
  .optional();

/** Coerces a URL query-string numeric range bound (e.g. "10") to a number; absent stays undefined. */
export const rangeNumberParam = z.coerce.number().optional();

/**
 * Zod shape for the `<id>_min`/`<id>_max` scalar filter fields of every cost
 * dimension in COST_RANGE_DIMENSIONS — one pair of `rangeNumberParam` entries
 * per dimension, generated instead of hand-listed so this is the ONLY place
 * that spells out each cost dimension's two filter field names.
 */
export type CostRangeFilterShape = {
  [K in CostRangeDimensionId as `${K}_min`]: typeof rangeNumberParam;
} & {
  [K in CostRangeDimensionId as `${K}_max`]: typeof rangeNumberParam;
};

export const costRangeFilterShape: CostRangeFilterShape = Object.fromEntries(
  COST_RANGE_DIMENSIONS.flatMap((d) => [
    [`${d.id}_min`, rangeNumberParam],
    [`${d.id}_max`, rangeNumberParam],
  ]),
) as CostRangeFilterShape;

export const sharedMediaUnitFilterFields = {
  region: filterArrayField.optional(),
  county: filterArrayField.optional(),
  agglomeration: filterArrayField.optional(),
  city: filterArrayField.optional(),
  /**
   * Selectable administrative-area IDs (stringified `admin_boundaries.id`
   * values, e.g. Warsaw districts). Fifth member of the location OR-group —
   * combines with region/county/agglomeration/city, not exclusion mode.
   */
  administrativeAreaIds: filterArrayField.optional(),
  formatCode: filterArrayField.optional(),
  /** Media-type dimension (array of MediaTypeEnum). Shared so it carries catalog→map. */
  mediaType: z.array(MediaTypeEnum).min(1).optional(),
  supplierId: filterArrayField.optional(),
  supplierNumber: filterArrayField.optional(),
  grwId: filterArrayField.optional(),
  lighting: filterArrayField.optional(),
  /** Proximity filter: OSM tag key (e.g. "amenity"). All 3 params required together. */
  near_poi_key: z.string().optional(),
  /** Proximity filter: OSM tag value (e.g. "cafe"). All 3 params required together. */
  near_poi_value: z.string().optional(),
  /** Proximity filter: radius in metres. All 3 params required together. */
  near_poi_radius_m: z.coerce.number().positive().optional(),
  /** Per-POI proximity filter: specific POI UUID. Mutually exclusive with near_poi_key/value. */
  near_poi_id: z.string().uuid().optional(),
  // ─── Numeric range filters (CU-86cataqt9, shared map-catalog-filter-parity) ───
  area_min: rangeNumberParam,
  area_max: rangeNumberParam,
  width_min: rangeNumberParam,
  width_max: rangeNumberParam,
  height_min: rangeNumberParam,
  height_max: rangeNumberParam,
  audience_min: rangeNumberParam,
  audience_max: rangeNumberParam,
  /**
   * Cost-record range filters (see COST_RANGE_DIMENSIONS). The API strips
   * only the internal-cost ones upstream when the caller lacks cost.view —
   * client price ranges are never stripped — see
   * stripCostRangeFiltersIfUnauthorized in apps/api/src/media-units/media-units.service.ts.
   */
  ...costRangeFilterShape,
  /** Material code (e.g. "siatka"), matches media_units.material. */
  material: filterArrayField.optional(),
  /** Multiformat flag value as stored (e.g. "true"/"false"), matches media_units.multiformat. */
  multiformat: filterArrayField.optional(),
  /** Tag codes (tag_definitions.code) — units having ANY of the given tags match. */
  tags: filterArrayField.optional(),
  /** Dedicated address text filter (distinct from the generic multi-column `search`). */
  address: z.string().optional(),
  /** Case-insensitive substring match on media_units.notes. */
  notes: z.string().optional(),
  /**
   * Case-insensitive substring match on media_unit_variants.road_number —
   * a unit matches when ANY of its variants has a matching road number
   * (EXISTS subquery, see build-filter-conditions.ts). Subsumes CU-86cb1nqcg.
   */
  roadNumber: z.string().optional(),
  /** "bez zdjęcia": when true, restrict to units with no photos. URL string "true"/"1" → true. */
  noPhoto: queryBoolean,
  /** Availability window start (YYYY-MM-DD). Applied only when availabilityTo is also set; a lone bound is ignored downstream. */
  availabilityFrom: isoDateString.optional(),
  /** Availability window end (YYYY-MM-DD). Applied only when availabilityFrom is also set; a lone bound is ignored downstream. */
  availabilityTo: isoDateString.optional(),
} as const;

/**
 * Shared `.superRefine()` body for every schema built from
 * sharedMediaUnitFilterFields that also carries availabilityFrom/To
 * (MediaUnitFiltersSchema, MapPinsQuerySchema): rejects an inverted
 * availability range and delegates to validateRangeFieldPairs for the
 * numeric range dimensions. Extracted here (not media-units.ts) so
 * MapPinsQuerySchema — which has no other reason to import media-units.ts —
 * can reuse the exact same validation instead of silently accepting
 * malformed/inverted ranges that MediaUnitFiltersSchema rejects.
 */
export function validateSharedMediaUnitFilterRefinements(
  value: Record<string, unknown>,
  ctx: z.RefinementCtx,
): void {
  const availabilityFrom = value.availabilityFrom as string | undefined;
  const availabilityTo = value.availabilityTo as string | undefined;
  // A single bound is allowed and simply ignored downstream: the DB layer
  // applies the availability predicate only when BOTH bounds are present
  // (see media-unit-availability-filter spec "Single bound is ignored").
  // Only reject an inverted range when both bounds are present.
  if (availabilityFrom !== undefined && availabilityTo !== undefined) {
    if (availabilityTo < availabilityFrom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'availabilityTo must not be earlier than availabilityFrom',
        path: ['availabilityTo'],
      });
    }
  }
  validateRangeFieldPairs(value, ctx);
}
